import React, { useState, useEffect } from "react";

import { Formik, Form } from "formik";
import { Error } from "icons";

import ingestionDataApi from "apis/ingestionData";
import { Pane, Typography, Button, Callout } from "clearsense-ui/components";
import { Select, Input, Checkbox } from "clearsense-ui/components/formik";

import {
  INITIAL_VALUES,
  CHART_TYPES,
  SINGLE_METRIC_CHARTS,
  ROW_LIMITS,
} from "../../../constants";
import { paneValidationSchema } from "../../../validations";

const ChartPane = ({
  formRef,
  modalRef,
  sortByModalRef,
  filterModalRef,
  showPane,
  tableDetails,
  editChart,
  addChartError,
  setShowPane,
  setShowModal,
  setShowSortByModal,
  setShowFilterModal,
  setTableDetails,
  setEditChart,
  handleSubmit,
}) => {
  const [isDBSchemaLoading, setIsDBSchemaLoading] = useState(false);
  const [isDBTablesLoading, setIsDBTablesLoading] = useState(false);
  const [isTableColumnsLoading, setIsTableColumnsLoading] = useState(false);
  const [rowLimitOptions, setRowLimitOptions] = useState(ROW_LIMITS);
  const [DBSchema, setDBSchema] = useState([]);
  const [schemaTables, setSchemaTables] = useState([]);
  const [options, setOptions] = useState([]);

  const fetchDBSchemas = async () => {
    setIsDBSchemaLoading(true);
    try {
      const res = await ingestionDataApi.fetchSchemas();
      setDBSchema(res.data.result);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsDBSchemaLoading(false);
    }
  };

  useEffect(() => {
    fetchDBSchemas();
  }, []);

  useEffect(() => {
    editChart?.schema &&
      fetchDBSchemaTables(JSON.parse(editChart?.schema)?.value);
    editChart?.table && fetchTableColumns(editChart?.table);
  }, [editChart?.id]);

  useEffect(() => {
    addChartError &&
      document
        ?.getElementById("callout")
        ?.scrollIntoView({ behavior: "smooth" });
  }, [addChartError]);

  const fetchDBSchemaTables = async schema => {
    setSchemaTables([]);
    setIsDBTablesLoading(true);
    try {
      const res = await ingestionDataApi.fetchSchemaTables(schema);
      setSchemaTables(res.data.result);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsDBTablesLoading(false);
    }
  };

  const fetchTableColumns = async table => {
    setIsTableColumnsLoading(true);
    try {
      const res = await ingestionDataApi.fetchTableColumns(table);
      setTableDetails(res.data.result);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsTableColumnsLoading(false);
    }
  };

  const resetDependantFields = formik => {
    formik.setFieldValue("namekey", "");
    formik.setFieldValue("valuekey", "");
    formik.setFieldValue("filters", "");
    formik.setFieldValue("sortBy", "");
  };

  return (
    <Formik
      enableReinitialize
      initialValues={
        Object.keys(editChart).length
          ? {
              title: editChart.title,
              schema: JSON.parse(editChart.schema),
              table: { label: editChart.table, value: editChart.table },
              chart_type: {
                label: editChart.chart_type,
                value: editChart.chart_type,
              },
              namekey: JSON.parse(editChart.group_by),
              valuekey: JSON.parse(editChart.metrics),
              filters: JSON.parse(editChart.filters),
              sortBy: JSON.parse(editChart.sort_by),
              sortDirection: editChart.sort_direction,
              row_limit: JSON.parse(editChart.limit),
            }
          : INITIAL_VALUES
      }
      validationSchema={paneValidationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
    >
      {formik => (
        <Form>
          <Pane
            data-cy="chart-pane"
            isOpen={showPane}
            onClose={() => {
              setShowPane(false);
              setEditChart({});
            }}
          >
            <Pane.Header>
              <Typography style="h3" type="bold" className="-mt-3">
                {Object.keys(editChart).length ? "Edit Chart" : "Add Chart"}
              </Typography>
            </Pane.Header>
            <Pane.Body className="py-4">
              <div className="w-full space-y-4">
                <Input
                  label="Title*"
                  name="title"
                  size="large"
                  placeholder="Enter the title"
                  data-cy="chart-title-input-field"
                />
                <Select
                  data-cy="chart-type-select-field"
                  required
                  name="chart_type"
                  label="Type"
                  placeholder="Select Chart Type"
                  options={CHART_TYPES}
                  onChange={e => {
                    resetDependantFields(formik);
                    formik.setFieldValue("sortDirection", true);
                    formik.setFieldValue("chart_type", e);
                  }}
                />
                <Select
                  required
                  name="schema"
                  label="DB Schema"
                  isLoading={isDBSchemaLoading}
                  options={DBSchema?.map(schema => ({
                    label: schema,
                    value: schema,
                  }))}
                  onChange={option => {
                    formik.setFieldValue("table", "");
                    resetDependantFields(formik);
                    formik.setFieldValue("schema", option);
                    fetchDBSchemaTables(option.value);
                  }}
                />
                <Select
                  required
                  name="table"
                  label="Database Table"
                  placeholder="Select Database Table"
                  isLoading={isDBTablesLoading}
                  options={schemaTables.map(tableName => ({
                    label: tableName[0],
                    value: tableName[0],
                  }))}
                  onChange={option => {
                    resetDependantFields(formik);
                    formik.setFieldValue("table", option);
                    fetchTableColumns(option.value);
                  }}
                />
                <Select
                  isMulti
                  required
                  isCreateable
                  isClearable={false}
                  name="namekey"
                  label="Group By"
                  placeholder="Select Table Column/Columns"
                  isLoading={isTableColumnsLoading}
                  onCreateOption={inputValue => {
                    setOptions(prev => [
                      { label: inputValue, value: inputValue },
                      ...prev,
                    ]);
                  }}
                  defaultOptions={[
                    ...options,
                    ...(formik?.values?.["table"]
                      ? tableDetails.map(columnName => ({
                          label: columnName[0],
                          value: columnName[0],
                        }))
                      : []),
                  ]}
                />
                <div
                  onClick={e => {
                    if (
                      !SINGLE_METRIC_CHARTS.includes(
                        formik.values["chart_type"]?.value
                      ) ||
                      (SINGLE_METRIC_CHARTS.includes(
                        formik.values["chart_type"]?.value
                      ) &&
                        (formik.values["valuekey"] === null ||
                          formik.values["valuekey"]?.length === 0))
                    ) {
                      if (
                        e.target?.classList?.[0]?.includes("cs-ui-react-select")
                      ) {
                        setShowModal(true);
                      }
                      modalRef.current.resetForm();
                    }
                  }}
                >
                  <Select
                    isMulti
                    required={formik.values["chart_type"].value !== "tabular"}
                    isClearable={false}
                    noOptionsMessage={() => null}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    name="valuekey"
                    label={
                      SINGLE_METRIC_CHARTS.includes(
                        formik.values["chart_type"]?.value
                      )
                        ? "Metric"
                        : "Metrics"
                    }
                    placeholder={
                      SINGLE_METRIC_CHARTS.includes(
                        formik.values["chart_type"]?.value
                      )
                        ? "Add a Metric"
                        : "Add Metric/Metrics"
                    }
                    onBlur={() => formik.validateForm()}
                  />
                </div>
                <div
                  onClick={e => {
                    if (
                      e.target?.classList?.[0]?.includes("cs-ui-react-select")
                    ) {
                      setShowFilterModal(true);
                    }
                    filterModalRef.current.resetForm();
                  }}
                >
                  <Select
                    isMulti
                    isClearable={false}
                    noOptionsMessage={() => null}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    name="filters"
                    label="Filters"
                    placeholder="Add Filter"
                  />
                </div>
                {!SINGLE_METRIC_CHARTS.includes(
                  formik.values["chart_type"]?.value
                ) && (
                  <div className="space-y-4">
                    <div
                      onClick={e => {
                        if (
                          formik.values["sortBy"] === null ||
                          formik.values["sortBy"]?.length === 0
                        ) {
                          if (
                            e.target?.classList?.[0]?.includes(
                              "cs-ui-react-select"
                            )
                          ) {
                            setShowSortByModal(true);
                          }
                          sortByModalRef.current.resetForm();
                        }
                      }}
                    >
                      <Select
                        isMulti
                        isClearable={false}
                        noOptionsMessage={() => null}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }}
                        name="sortBy"
                        label="Sort By"
                        placeholder="Add Sort By"
                        onBlur={() => formik.validateForm()}
                      />
                    </div>
                    <Checkbox
                      data-cy="sort-direction-check-box"
                      name="sortDirection"
                      label="Sort Descending"
                      checked={formik.values["sortDirection"]}
                      onChange={e =>
                        formik.setFieldValue("sortDirection", e.target.checked)
                      }
                    />
                  </div>
                )}
                <Select
                  required
                  isCreateable
                  name="row_limit"
                  label="Row Limit"
                  placeholder="Select Row Limit"
                  defaultOptions={rowLimitOptions.sort(
                    (a, b) => a.value - b.value
                  )}
                  isValidNewOption={newOption =>
                    newOption && Number.isInteger(Number(newOption))
                  }
                  onCreateOption={inputValue =>
                    setRowLimitOptions([
                      ...rowLimitOptions,
                      { label: inputValue, value: inputValue },
                    ])
                  }
                />
                {addChartError && (
                  <Callout
                    data-cy="chart-pane-callout"
                    id="callout"
                    size="large"
                    style="danger"
                    icon={Error}
                  >
                    {addChartError}
                  </Callout>
                )}
              </div>
            </Pane.Body>
            <Pane.Footer className="space-x-2">
              <Button
                style="primary"
                type="submit"
                label={Object.keys(editChart).length ? "Save" : "Add"}
                data-cy="chart-submit-button"
                onClick={() => formik.submitForm()}
                disabled={!formik.dirty || formik.isSubmitting}
                loading={formik.isSubmitting}
              />
              <Button
                style="secondary"
                label="Cancel"
                data-cy="chart-cancel-button"
                onClick={() => {
                  setShowPane(false);
                  setEditChart({});
                  formik.resetForm();
                }}
              />
            </Pane.Footer>
          </Pane>
        </Form>
      )}
    </Formik>
  );
};

export default ChartPane;
